import { useApolloClient } from '@apollo/client';
import { message } from 'antd';
import { Formik } from 'formik';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type ConfiguratorModuleSpecsFragment } from '../../../../api/fragments/ConfiguratorModuleSpecs';
import {
    UpdateConfiguratorModuleEmailContentsDocument,
    type UpdateConfiguratorModuleEmailContentsMutation,
    type UpdateConfiguratorModuleEmailContentsMutationVariables,
} from '../../../../api/mutations/updateConfiguratorModuleEmailContents';
import { EmailContentUpdateType } from '../../../../api/types';
import Form from '../../../../components/fields/Form';
import useHandleError from '../../../../utilities/useHandleError';
import useValidator from '../../../../utilities/useValidator';
import validators from '../../../../utilities/validators';
import ConfiguratorModuleEmailForm from './ConfiguratorModuleEmailForm';
import { type ConfiguratorEmailFormValues } from './type';
import useHandleConfiguratorModuleAssets from './useHandleConfiguratorModuleAssets';

type ConfiguratorModuleEmailProps = {
    module: ConfiguratorModuleSpecsFragment;
};

const validator = validators.compose(
    validators.requiredString('saveOrder.subject.defaultValue.defaultValue'),
    validators.requiredString('saveOrder.introTitle.defaultValue.defaultValue'),
    validators.requiredString('saveOrder.contentText.defaultValue.defaultValue'),

    validators.requiredString('submitOrder.subject.defaultValue.defaultValue'),
    validators.requiredString('submitOrder.introTitle.defaultValue.defaultValue'),
    validators.requiredString('submitOrder.contentText.defaultValue.defaultValue'),
    validators.requiredBoolean('submitOrder.isPaymentSectionShown')
);

const ConfiguratorModuleEmail = ({ module }: ConfiguratorModuleEmailProps) => {
    const { t } = useTranslation(['configuratorModuleDetail', 'emails']);
    const apolloClient = useApolloClient();
    const validate = useValidator(validator);

    const handleConfiguratorModuleAssets = useHandleConfiguratorModuleAssets();

    const initialValues: ConfiguratorEmailFormValues = useMemo((): ConfiguratorEmailFormValues => {
        if (module.emailContents) {
            return module.emailContents;
        }

        return {
            saveOrder: {
                subject: {
                    defaultValue: {
                        defaultValue: t('emails:configurator.saveOrder.subject'),
                        overrides: [],
                    },
                    overrides: [],
                },
                introTitle: {
                    defaultValue: {
                        defaultValue: t('emails:configurator.saveOrder.introTitle'),
                        overrides: [],
                    },
                    overrides: [],
                },
                contentText: {
                    defaultValue: {
                        defaultValue: t('emails:configurator.saveOrder.contentText'),
                        overrides: [],
                    },
                    overrides: [],
                },
            },
            submitOrder: {
                subject: {
                    defaultValue: {
                        defaultValue: t('emails:configurator.submitOrder.subject'),
                        overrides: [],
                    },
                    overrides: [],
                },
                introTitle: {
                    defaultValue: {
                        defaultValue: t('emails:configurator.submitOrder.introTitle'),
                        overrides: [],
                    },
                    overrides: [],
                },
                contentText: {
                    defaultValue: {
                        defaultValue: t('emails:configurator.submitOrder.contentText'),
                        overrides: [],
                    },
                    overrides: [],
                },
                isPaymentSectionShown: true,
            },
        };
    }, [module.emailContents, t]);

    const onSubmit = useHandleError(
        async ({ submitOrder, saveOrder }: ConfiguratorEmailFormValues) => {
            const { introImage: saveOrderIntroImage, bannerImage, ...saveOrderSetting } = saveOrder;
            const { introImage: submitOrderIntroImage, ...submitOrderSetting } = submitOrder;

            message.loading({
                content: t('configuratorModuleDetail:messages.submitting'),
                key: 'primary',
                duration: 0,
            });

            await apolloClient
                .mutate<
                    UpdateConfiguratorModuleEmailContentsMutation,
                    UpdateConfiguratorModuleEmailContentsMutationVariables
                >({
                    mutation: UpdateConfiguratorModuleEmailContentsDocument,
                    variables: {
                        moduleId: module.id,
                        settings: {
                            emailContentUpdateType: EmailContentUpdateType.Module,
                            saveOrder: saveOrderSetting,
                            submitOrder: submitOrderSetting,
                        },
                    },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            await handleConfiguratorModuleAssets(
                module.id,
                { submitOrder, saveOrder },
                EmailContentUpdateType.Module,
                module.emailContents
            );

            await message.success({
                content: t('configuratorModuleDetail:messages.success'),
                key: 'primary',
            });
        },
        [apolloClient, handleConfiguratorModuleAssets, module.emailContents, module.id, t]
    );

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
            {({ handleSubmit }) => (
                <Form
                    id="updateEmailForm"
                    name="updateEmailForm"
                    onSubmitCapture={handleSubmit}
                    style={{ marginTop: -16 }}
                >
                    <ConfiguratorModuleEmailForm companyId={module.companyId} defaultValueDisabled={false} />
                </Form>
            )}
        </Formik>
    );
};

export default ConfiguratorModuleEmail;
